import React from 'react';
import {
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  insertList,
  ListNode,
  ListItemNode,
  removeList,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ToolbarButton } from '../../styles';
import orderedList from 'assets/icons/rich-text-editor-v3/orderedList.svg';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW } from 'lexical';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import useUpdateListener from '../../use-update-listener';

const useOrderedListPluginRegisters = () => {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    if (!editor.hasNode(ListNode) || !editor.hasNode(ListItemNode)) {
      throw new Error('Lexical Editor: List plugin requires ListNode and ListItemNode to be registered');
    }

    return mergeRegister(
      editor.registerCommand(INSERT_ORDERED_LIST_COMMAND, () => {
        insertList(editor, 'number');
        return true;
      }, COMMAND_PRIORITY_LOW),
      editor.registerCommand(REMOVE_LIST_COMMAND, () => {
        removeList(editor);
        return true;
      }, COMMAND_PRIORITY_LOW),
    );
  }, [editor]);
};

export const OrderedListPlugin = () => {
  useOrderedListPluginRegisters();
  const [active, setActive] = React.useState(false);
  const [editor] = useLexicalComposerContext();

  const update = React.useCallback(() => {
    const selection = $getSelection();
    let isActive = false;
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const node = $getNearestNodeOfType(element, ListNode);
      isActive = Boolean(node?.getListType() === 'number');
    }

    if (isActive !== active) setActive(isActive);
  }, [active]);

  useUpdateListener(editor, update);

  const toggle = React.useCallback(() => {
    if (active) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [active, editor]);

  return (
    <ToolbarButton
      active={active}
      dangerouslySetInnerHTML={{ __html: orderedList }}
      onClick={toggle}
    />
  );
};

export default OrderedListPlugin;
